<script setup lang="ts">
/**
 * SG AGM 4종 프리셋 스튜디오 공통 패널 — 기존 폼 모델이 단일 원본.
 *   · 문서 칸(CddVarField) ↔ 모델: useNoticeStudioSync + 모델 변경 시 자동 push
 *   · 자동채움/✨ 반영: useAgmStudioEntityLogic (기존 Agm*Form.vue 로직 그대로)
 *   · TASK_DATA / L_TASK_EFILING 없음(기존과 동일) / 문서 저장: 기존 build*PresetPrepareDocuments 그대로
 *   · Create 시 필수 칸 검사 없음(스튜디오 공통) — 제목·회사만 확인.
 */
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import type { OfficerDetail, SignerEntry } from '@/features/AI-documents/types/docGen.types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { fetchEntityAddresses } from '@/shared/composables/useEntityAddresses';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { provideAgmFill, typeOf } from '../../../preset/components/final-dividends/cdd/useCddFill';
import { useNoticeStudioSync } from '../../controller/notice-studio/useNoticeStudioSync';
import { restoreReactiveFormFields } from '../agmFormStateRestore';
import { buildAgmMemberRepresentativeLookupContext, buildAgmSoleMemberNameLookupContext } from '../agmMemberPersonLookup';
import { dateToHtmlInput } from '../../change-financial-year-end/composables/dateToHtmlInput';
import { buildSgPresetPrepareDocumentItem } from '../../presetDocCustSave';
import { provideAgmStudio, signersFromVar, type AgmAutoSigner } from './agmStudioContext';
import { useAgmStudioEntityLogic } from './useAgmStudioEntityLogic';
import AgmStudioPackage from './AgmStudioPackage.vue';
import type { AgmStudioConfig, AgmStudioDocSlot } from './agmStudioConfig';

const props = defineProps<{
  config: AgmStudioConfig;
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: Record<string, any> | null;
  editTaskId?: number | null;
  studio?: boolean;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'request-person-lookup', context: Record<string, any>): void;
  (e: 'request-address-lookup', context: Record<string, any>): void;
  (e: 'progress-change', pct: number): void;
}>();

const cfg = props.config;
const router = useRouter();
const fill = provideAgmFill();
const formModel = reactive<Record<string, any>>(cfg.createModel());
const sync = useNoticeStudioSync(fill, formModel, cfg.fieldMap as any, cfg.display as any);
// 모델이 자동채움·✨·복원으로 바뀌면 문서 칸에 반영
watch(() => cfg.fieldMap.map(([, f]) => formModel[f]), () => sync.push(), { immediate: true });

const creating = ref(false);
const progress = ref('');
const taskConfigSteps = ref<string[]>([]);
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });

const studio = computed(() => !!props.studio);
const editMode = computed(() => !!props.editTaskId);
const effectiveTaskTitle = computed(() => (props.taskTitle || '').trim());
const icpCd = computed(() => String(props.entityData?.icpCd || props.entityData?.entityCd || '').trim());
const st = (v: unknown) => (v == null ? '' : String(v).trim());

function emitProgress() {
  const total = fill.totalFields.value || 1;
  emit('progress-change', Math.min(100, Math.round((fill.filledCount.value / total) * 100)));
}
watch([() => fill.filledCount.value, () => fill.totalFields.value], emitProgress, { immediate: true });

// ── 자동채움 / ✨ 반영 (기존 폼 로직) ──
const logic = useAgmStudioEntityLogic({
  model: formModel,
  entityCd: icpCd,
  editMode,
  applyProfile: cfg.applyProfile,
  withAuditor: cfg.withAuditor,
  withChairman: cfg.withChairman,
  selectedPerson: () => props.selectedPerson,
  activePersonContext: () => props.activePersonContext,
  tag: cfg.tag,
});

// 회사 선택 → 회사명·등록번호·FYE (기존 패널과 동일, Edit 모드는 복원값 유지)
watch(
  () => props.entityData,
  (ent) => {
    if (!ent || editMode.value) return;
    const name = st(ent.companyName || ent.entityName || ent.ENTITY_NAME);
    const reg = st(ent.registrationNo || ent.regNo || ent.REG_NO);
    if (name) formModel.companyName = name;
    if (reg) formModel.companyRegistrationNo = reg;
    const fye = dateToHtmlInput(ent.financialYearEnd ?? ent.FINANCIAL_YEAR_END);
    if (fye && !st(formModel.financialYearEndDate)) formModel.financialYearEndDate = fye;
    if ('registeredOfficeAddress' in formModel && !st(formModel.registeredOfficeAddress)) {
      const addr = st(ent.registeredAddress || ent.address);
      if (addr) formModel.registeredOfficeAddress = addr;
    }
  },
  { immediate: true, deep: true },
);

function lookup(ctx: Record<string, any>) {
  emit('request-person-lookup', { ...ctx, entityCd: icpCd.value || ctx.entityCd, icpCd: icpCd.value || ctx.icpCd });
}
fill.setActivateHandler((key: string) => {
  if (typeOf(key) === 'entity') { emit('change-entity'); return; }
  if (key === 'chairman_name') lookup({ fieldGroup: 'chairman', defaultRole: 'DIRECTOR' });
  else if (key === 'auditor_name') lookup({ fieldGroup: 'auditor', defaultRole: 'AUDITOR' });
  else if (key === 'member_representative_name') lookup(buildAgmMemberRepresentativeLookupContext());
  else if (key === 'member_name' && (formModel.memberType === 'sole_corporate' || formModel.memberType === 'sole_individual')) {
    lookup(buildAgmSoleMemberNameLookupContext(formModel.memberType));
  }
});
/**
 * 📍 챗봇 주소
 *   · Registered Office — 엔티티 주소가 2개 이상이면 목록, 아니면 직접 입력
 *   · AGM Venue(장소) — 회의 장소라 엔티티 주소 목록을 쓰지 않고 항상 직접 입력
 */
const ADDRESS_FIELDS: Record<
  string,
  { fieldGroup: string; modelKey: string; subjectLabel: string; alwaysDirect?: boolean }
> = {
  registered_office_address: {
    fieldGroup: 'registeredOfficeAddress',
    modelKey: 'registeredOfficeAddress',
    subjectLabel: 'Registered Office',
  },
  agm_venue: {
    fieldGroup: 'agmVenue',
    modelKey: 'agmVenue',
    subjectLabel: 'AGM Venue',
    alwaysDirect: true,
  },
};
fill.setAddressHandler(async (key: string) => {
  const bare = key.includes('/') ? key.slice(key.lastIndexOf('/') + 1) : key;
  const spec = ADDRESS_FIELDS[bare];
  if (!spec) return;

  let addresses: Array<{ label: string; value: string; type: string }> = [];
  if (!spec.alwaysDirect && icpCd.value) {
    try {
      const profile = await fetchEntityAddresses(icpCd.value);
      addresses = (profile?.addresses || [])
        .filter((a) => st(a.value))
        .map((a) => ({ label: a.label, value: a.value, type: a.type }));
    } catch { /* ignore */ }
  }

  const useList = !spec.alwaysDirect && addresses.length > 1;
  emit('request-address-lookup', {
    fieldGroup: spec.fieldGroup,
    scopedKey: key,
    index: 0,
    subjectLabel: spec.subjectLabel,
    addresses: useList ? addresses : [],
    // ⚠ directInput 이 true 여야 address-input 제출값이 selectedAddress 로 들어온다
    directInput: !useList,
  });
});
watch(() => props.selectedAddress, (sel) => {
  if (!sel?.value) return;
  const fg = st(props.activeAddressContext?.fieldGroup);
  const hit = Object.values(ADDRESS_FIELDS).find((s) => s.fieldGroup === fg);
  if (!hit) return;
  formModel[hit.modelKey] = sel.value;
});

// ── 문서 목록 ──
const selectionDocs = computed(() => {
  const seen = new Set<string>();
  return (props.selection.documents || []).filter((d: any) => {
    const k = String(d?.tmpId || d?.templateName || '');
    if (!k || seen.has(k)) return false; seen.add(k); return true;
  });
});
const docSlots = computed<AgmStudioDocSlot[]>(() => selectionDocs.value.map((d: any, i: number) => {
  const name = String(d.templateName || d.docCode || `Document ${i + 1}`);
  const kind = cfg.classify(name);
  const def = cfg.docFor(kind);
  const inc = cfg.include(kind, formModel);
  return def ? { key: `${d.tmpId || i}`, name: name.replace(/\.docx?$/i, ''), comp: def.comp, props: def.props, included: inc.included, reason: inc.reason } : null;
}).filter(Boolean) as AgmStudioDocSlot[]);

/** 문서 [[Signers]] 기본값 — 기존 문서 생성 함수를 그 문서 1건으로 돌려 나온 signers 를 그대로 쓴다. */
function signerList(docKind: string): AgmAutoSigner[] {
  const d: any = selectionDocs.value.find((x: any) => cfg.classify(String(x.templateName || '')) === docKind);
  if (!d) return [];
  try {
    const out = cfg.buildPrepareDocuments({
      selectionDocuments: [{ tmpId: d.tmpId || 1, templateName: d.templateName }],
      commonFormData: {}, formModel, personName: '', entityCd: icpCd.value || undefined,
    });
    const doc = out[0];
    if (!doc) return [];
    const fromArr = signersFromVar(doc.formData?.signers ?? doc.formData?.Signers);
    return fromArr.length ? fromArr : signersFromVar(doc.signers);
  } catch { return []; }
}
// 회원 구성 확인 게이트 — 회사를 고르면 문서 전에 판별 결과를 확인받는다(Edit 복원·확인 후엔 바로 문서).
const gateConfirmed = ref(false);
/** 문서에서 고친 서명자(키 `${docKind}_Signers`) — 회사를 바꾸면 비우고 새 회사 기본값으로 */
const docSigners = reactive<Record<string, SignerEntry[]>>({});
function clearDocSigners() { for (const k of Object.keys(docSigners)) delete docSigners[k]; }
watch(icpCd, (n, o) => { if (o && n !== o) { gateConfirmed.value = false; clearDocSigners(); } });
provideAgmStudio({
  model: formModel as any, lookup, signerList, docSigners, entityCd: icpCd,
  gate: { confirmed: gateConfirmed, detected: logic.detectedMemberType, summary: logic.memberSummary, loading: logic.loading, loaded: logic.loaded },
});

onMounted(async () => {
  emitProgress();
  try {
    const code = props.selection.task.TASK_CODE;
    if (code) { const c = await getTaskConfigByCode(code); taskConfigSteps.value = c?.processSteps || []; }
  } catch { /* ignore */ }
});

// ── Edit 지원 — 기존 패널과 같은 스냅샷 형식 ──
function gatherState(): Record<string, any> {
  const snap = JSON.parse(JSON.stringify(formModel));
  const signersSnap = JSON.parse(JSON.stringify(docSigners));
  return cfg.snapshot === 'nested' ? { formModel: snap, agmDocSigners: signersSnap } : { ...snap, agmDocSigners: signersSnap };
}
function restoreState(s: Record<string, any> | null | undefined) {
  if (!s || typeof s !== 'object') return;
  const src = (s.formModel && typeof s.formModel === 'object') ? s.formModel : s;
  try { restoreReactiveFormFields(formModel as Record<string, unknown>, JSON.parse(JSON.stringify(src))); } catch { /* ignore */ }
  clearDocSigners();
  if (s.agmDocSigners && typeof s.agmDocSigners === 'object') Object.assign(docSigners, JSON.parse(JSON.stringify(s.agmDocSigners)));
  sync.push();
  gateConfirmed.value = true; // 저장된 태스크 — 구성 확인은 이미 끝남
  emitProgress();
}
defineExpose({ gatherState, restoreState, createTask: handleCreateTask });

/** 문서에서 서명자를 고친 경우 그 문서의 signers 를 고친 목록으로 바꾼다(안 고친 문서는 기존 생성 로직 그대로). */
function applyEditedSigners(docs: Array<Record<string, any>>): Array<Record<string, any>> {
  return docs.map((doc) => {
    const key = `${cfg.classify(String(doc.label || ''))}_Signers`;
    if (!(key in docSigners)) return doc;
    const list = docSigners[key]
      .filter((x) => st(x.name))
      .map((x) => ({ name: st(x.name), email: st(x.email), role: st(x.role) || 'signer', contactCd: st((x as any).contactCd) }));
    const json = list.length ? JSON.stringify(list) : '';
    const fd = { ...(doc.formData || {}), signers: json, Signers: json };
    return buildSgPresetPrepareDocumentItem({
      templateId: doc.templateId, templateName: String(doc.label || ''), personName: String(doc.personName || ''),
      commonFormData: fd, entityCd: doc.entityCd,
    });
  });
}

async function handleCreateTask() {
  const title = effectiveTaskTitle.value;
  if (!title) { progress.value = 'Error: enter a Task Title in the chat first.'; return; }
  if (!props.entityData) { progress.value = 'Error: select an entity in the chat first.'; return; }
  const taskTypeCd = props.selection.task.TASK_CODE;
  if (!taskTypeCd) { progress.value = 'Error: Task code missing on preset.'; return; }

  creating.value = true;
  progress.value = editMode.value ? 'Updating task...' : 'Creating task...';
  try {
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing') || steps.includes('efiling'),
    };
    const taskFormData: Record<string, any> = { ...cfg.buildTaskFormData(formModel, icpCd.value || ''), presetForm: gatherState() };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id.'; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ taskData: { formData: taskFormData } }) });
      try { await fetch(`/api/startTask/tasks/${taskId}/title`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ taskTitle: title }) }); } catch { /* optional */ }
    } else {
      const taskJson = await fetch('/api/startTask/tasks', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ entityCd: icpCd.value || null, taskTypeCd, taskTypeName: props.selection.task.TASK_NAME, taskTitle: title, taskFlow, officerData: taskFormData }),
      }).then((r) => r.json());
      taskId = taskJson?.data?.taskId || taskJson?.taskId || null;
      if (!taskId) { progress.value = `Error: ${taskJson?.error?.message || taskJson?.message || 'Task creation failed'}`; return; }
    }

    progress.value = 'Saving documents...';
    const entityName = st(props.entityData?.companyName || props.entityData?.entityName || props.entityData?.ENTITY_NAME);
    const docsToSave = applyEditedSigners(cfg.buildPrepareDocuments({
      selectionDocuments: props.selection.documents || [],
      commonFormData: cfg.toTemplateVariables(formModel),
      formModel,
      personName: entityName || st(formModel.companyName),
      entityCd: icpCd.value || undefined,
    }));
    if (docsToSave.length) {
      try {
        const r = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        }).then((x) => x.json());
        if (!r?.success) progress.value = `Documents save warning: ${r?.message || 'unknown'}`;
      } catch (err) { console.warn(`[${cfg.tag}] prepare-documents failed`, err); }
    }
    try { await fetch(`/api/startTask/tasks/${taskId}/document-status`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include' }); } catch { /* ignore */ }
    try { await fetch('/api/startTask/preset/use-log', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }) }); } catch { /* ignore */ }
    emit('task-created', { taskId, taskTitle: title });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (err: unknown) {
    console.error(`[${cfg.tag}] create task failed:`, err);
    progress.value = `Error: ${err instanceof Error ? err.message : String(err)}`;
  } finally { creating.value = false; }
}
</script>

<template>
  <div class="ag-panel" :class="{ studio }">
    <div v-if="!studio" class="co-head">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME || config.title }}</div>
        <div class="co-subtitle">{{ config.subtitle }}</div>
      </div>
      <div class="co-actions">
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-task-title')">Change title</v-btn>
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-entity')">Change company</v-btn>
      </div>
    </div>
    <v-progress-linear v-if="logic.loading.value" indeterminate color="primary" height="2" />
    <div class="co-doc">
      <AgmStudioPackage :docs="docSlots" />
    </div>
    <div v-if="!studio" class="co-foot">
      <v-btn color="primary" class="text-none" :loading="creating" :disabled="!entityData || !effectiveTaskTitle || creating" @click="handleCreateTask">
        {{ editMode ? 'Update task' : 'Create Task' }}
      </v-btn>
      <span v-if="progress && !creating" class="co-err">{{ progress }}</span>
    </div>
    <div v-else-if="progress && !creating" class="co-foot"><span class="co-err">{{ progress }}</span></div>
  </div>
</template>

<style scoped>
.ag-panel { display: flex; flex-direction: column; gap: 12px; min-height: 0; }
.ag-panel.studio .co-foot { position: sticky; bottom: 0; background: #fff; padding: 10px 0; z-index: 2; }
.co-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.co-title { font-size: 16px; font-weight: 800; color: #1a2336; }
.co-subtitle { font-size: 12px; color: #6b7a94; margin-top: 2px; }
.co-actions { display: flex; gap: 4px; flex-shrink: 0; }
.co-doc { flex: 1; min-height: 0; }
.co-foot { display: flex; align-items: center; gap: 12px; }
.co-err { font-size: 12px; color: #c0392b; }
</style>
